import React, { Fragment, useContext, useEffect, useState } from 'react'
import Link from '@/components/Link'
import { BellIcon, CashIcon, ChatIcon, CheckIcon, ChevronDownIcon, CogIcon, LogoutIcon, MenuAlt2Icon, MenuIcon, UserGroupIcon, UserIcon } from '@heroicons/react/solid'
import { useFirebase, useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { useSelector } from 'react-redux';
import { LayoutContext } from '@/contexts/LayoutContext'
import Button from '@/components/Button'
import modalRoutes from '@/modalRoutes'
import { AuthContext } from '@/contexts/AuthContext'
import { Menu, Transition, Popover } from '@headlessui/react'
import { convertAmount } from '@/utils/currencyResolver'
import { usePrevious } from '@/hooks/usePrevious'
import { classNames } from '@/utils/classNames'
import { ImBubbles2 } from "react-icons/im";

export default function Header() {
  const { auth } = useContext(AuthContext);
  const { toggleSidebar } = useContext(LayoutContext);
  return (
    <div className="bg-gray-900 z-40 z-[100]">
      <nav className="flex h-16 items-center justify-between px-4 lg:px-6 mx-auto">
        <div className="hidden md:flex items-center md:space-x-4">
          <button className="hidden md:block text-gray-300 hover:text-white" onClick={toggleSidebar}>
            <span><MenuAlt2Icon className="h-5 w-5" /></span>
          </button>
          <Link className="tracking-wide" style={{transform: 'rotate(-2deg) translateY(-2px)', fontSize: 26}} to="/">
            <span className="logo">Duels</span>
          </Link>
        </div>
        {auth.isLoaded && !auth.isEmpty && (
          <div className="flex shadow-xl">
            <Balance />
            <Wallet />
          </div>
        )}
        <div className="flex items-center space-x-4">
          {/* <GuestControls /> */}
          {auth.isLoaded && !auth.isEmpty ? <UserControls /> : <GuestControls />}
        </div>
      </nav>
    </div>
  )
}

const GuestControls = () => {
  return (
    <>
      <Link className="font-medium" to={modalRoutes.LOGIN}>
        <span>Login</span>
      </Link>
      <Button link primary to={modalRoutes.REGISTER} rounded="rounded-full" className="px-4 py-2.5">
        Register
      </Button>
    </>
  )
}

const UserControls = () => {
  const { toggleChat, chat } = useContext(LayoutContext)
  return (
    <>
      {/* <Notifications /> */}
      <div className="hidden md:flex">
        <button className={classNames('rounded-full p-[10px] hover:text-white transition duration-300', chat ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white' : 'bg-gray-800 text-gray-300')} onClick={toggleChat}>
          <ImBubbles2
            className="w-4 h-4"
          />
        </button>
      </div>
      <Settings />
    </>
  )
}

const Settings = () => {
  const { user } = useContext(AuthContext)
  const SettingsItem = ({ icon, label, to, ...rest }) => (
    <Link {...rest} className="flex items-center p-2 hover:bg-gray-900 hover:text-white" to={to}>
      <span className="w-5 h-5 mr-2">{icon}</span>
      <span>{label}</span>
    </Link>
  )
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div className="flex">
        <Menu.Button className="inline-flex justify-center w-full text-sm font-medium text-white rounded-md bg-opacity-20 hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
          {/* <span><UserIcon className="w-5 h-5" /></span> */}
          <span className="flex items-center font-medium pl-3 pr-2 text-white hover:text-gray-300 rounded py-1">
            {user?.username || '...'}
            <ChevronDownIcon className="w-4 ml-2" />
          </span>
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 w-40 mt-2 origin-top-right bg-gray-800 text-gray-300 font-medium divide-y divide-gray-100 rounded-md shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="text-sm py-1">
            <Menu.Item>
              <SettingsItem label="Wallet" icon={<CashIcon />} to={modalRoutes.WALLET} />
            </Menu.Item>
            <Menu.Item>
              <SettingsItem label="Account" icon={<UserIcon />} to="/account" />
            </Menu.Item>
            <Menu.Item>
              <SettingsItem label="Settings" icon={<CogIcon />} to="/settings" />
            </Menu.Item>
            <Menu.Item>
              <SettingsItem label="Logout" icon={<LogoutIcon />} to={modalRoutes.LOGOUT} />
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

const Balance = () => {
  const { user } = useContext(AuthContext);
  const [userBalance, setUserBalance] = useState(0)
  const [prevBalance, setPrevBalance] = useState(0)

  useEffect(() => {
    setUserBalance(prevState => {
      setPrevBalance(prevState)
      return user?.balance
    })
  }, [user?.balance])

  return (
    <>
      <div className="bg-gray-800 text-center group px-4 py-1.5 items-center rounded-full pr-10 -mr-6 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 relative">
        <span className="flex items-center text-green-600"><img className="w-6" src="/icons/GP_COIN_03.svg" alt="" />{convertAmount(userBalance)}</span>
        {prevBalance > userBalance ? (
          <p key={userBalance} className="absolute transition duration-300 animate-balance text-red-500">-{convertAmount(prevBalance - userBalance)}</p>
        ) : (
          <p key={userBalance} className="absolute transition duration-300 animate-balance text-green-500">+{convertAmount(userBalance - prevBalance)}</p>
        )}
      </div>
    </>
  )
}

const Wallet = () => {
  return (
    <Button className="text-sm px-5 font-medium flex items-center" rounded="rounded-full" link to={modalRoutes.WALLET}>
      <span>Wallet</span>
    </Button>
  )
}

// const Notifications = () => {
//   // const { notifications } = useContext(AuthContext)
//   const hasUnreadNotifications = [];
//   const notifications = [];
//   //const hasUnreadNotifications = getNotifications().filter(({ wasShown }) => !wasShown)
//   return (
//     <Popover className="relative">
//       {({ open, close }) => (
//         <>
//           <Popover.Button
//             className="relative flex justify-center w-full"
//           >
//             <span><BellIcon className="w-5 h-5" /></span>
//             {hasUnreadNotifications?.length > 0 && (
//               <>
//                 <span className="block bg-red-500 h-2 w-2 rounded-full absolute bottom-0 right-0 animate-ping"></span>
//                 <span className="block bg-red-500 h-2 w-2 rounded-full absolute bottom-0 right-0"></span>
//               </>
//             )}
//           </Popover.Button>
//           <Transition
//             as={Fragment}
//             enter="transition ease-out duration-200"
//             enterFrom="opacity-0 translate-y-1"
//             enterTo="opacity-100 translate-y-0"
//             leave="transition ease-in duration-150"
//             leaveFrom="opacity-100 translate-y-0"
//             leaveTo="opacity-0 translate-y-1"
//           >
//             <Popover.Panel className="fixed md:absolute z-10 inset-x-0 md:left-auto w-full md:w-80 md:right-0 px-4 mt-3 transform sm:px-0">
//               <div className="overflow-hidden bg-white rounded-md shadow-lg max-h-[400px] flex flex-col notifications">
//                 <div className="flex justify-between pb-3 border-b p-4">
//                   <p className="text-lg font-medium text-gray-900">Notifications</p>
//                   <button className="text-blue-600 text-xs">Mark all as read</button>
//                 </div>
//                 {notifications?.length > 0 ? (
//                   <div className="flex-1 overflow-auto px-4 pb-4">
//                     {notifications.map(({ type, message }) => (
//                       <div className="py-4 last:pb-0 relative flex border-b last:border-b-0">
//                         <span className="block h-8 w-8 rounded-full shadow-md bg-yellow-500"></span>
//                         <div className="flex-1 ml-3">
//                           <div className="flex justify-between">
//                             <p className="font-medium text-gray-900">patel</p>
//                             <p className="text-gray-300 text-xs">2m ago</p>
//                           </div>
//                           <p className="text-gray-500 text-xs">{message}</p>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <div className="py-6 text-center text-gray-900 font-medium">No new messages.</div>
//                 )}
//                 <div>
//                   <Link onClick={close} to="/settings" className="p-4 flex bg-gray-200 hover:bg-gray-300 transition duration-300 items-center justify-center text-gray-600 font-medium">
//                     <span>View all</span>
//                   </Link>
//                 </div>
//               </div>
//             </Popover.Panel>
//           </Transition>
//         </>
//       )}
//     </Popover>
//   )
// }